import { engineComponent } from "neo-common";
import { Communication } from "neo-games";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../Utils/FrameTimer";
import { Paylines } from "../Paylines";
import { Symbols } from "../Symbols";
import { Machine } from "../Machine";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { StageController } from "../../Components/StageController";
import { WinAnimator } from "../WinAnimator";
import { FreespinsController } from "./FreespinsController";
import { GameManager } from "../../Managers/GameManager";
import { PlayList } from "../../Components/Sounds/PlayList";
import { HtmlController } from "../../HTMLComponents/HtmlController";
import { DataManager } from "../../Managers/DataManager";
import { Tweens } from "../../Components/Effects/Tweens";
import { WinAnimatorExtended } from "../../CustomComponents/WinAnimatorExtended";
import { TeasingBonus } from "../../CustomComponents/TeasingBonus";
import { MachineExtended } from "../../CustomComponents/MachineExtended";
import { AmbientBackground } from "../../Utils/AmbientBackground";

@engineComponent(ComponentTypes.DisplayObject)
export class FreespinsView extends ContainerElement {
    static INTRO_DELAY: number = 10;
    static OUTRO_DELAY: number = 90;

    private _controller: FreespinsController;

    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;

    private _paylines: Paylines;
    private _symbols: Symbols;
    private _machine: MachineExtended;
    private _winAnimator: WinAnimatorExtended;
    private _noWinContainer: ContainerElement;
    private _winContainer: ContainerElement;

    private _initWild: Array<boolean> = [false, false, false, false, false];

    private _teasingBonus: TeasingBonus;

    private _background: SwitchElement;
    private _logoBlackTransition: SwitchElement;
    private _transition: SwitchElement;

    private _fsMessageStart: HTMLElement;
    private _fsMessageEnd: HTMLElement;
    private _fsMessageEndNoWin: HTMLElement;

    constructor() {
        super();

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        StageController.freespinsStageContainer = this;

        this._teasingBonus = new TeasingBonus();
    }

    createByNode(node: Element): void {
        super.createByNode(node);

        if (this.childs != null) {
            for (let key in this.childs) {
                if (this.childs[key] instanceof Paylines) {
                    this._paylines = this.childs[key];
                    this._paylines.reset();
                }

                if (this.childs[key] instanceof Symbols) {
                    this._symbols = this.childs[key];
                    this.removeChild(this._symbols);
                }

                if (this.childs[key] instanceof Machine) {
                    this._machine = this.childs[key];
                }

                if (this.childs[key] instanceof WinAnimatorExtended) {
                    this._winAnimator = this.childs[key];
                }

                if (key === "NoWinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._noWinContainer = this.childs[key];
                }

                if (key === "WinContainer" && this.childs[key] instanceof ContainerElement) {
                    this._winContainer = this.childs[key];
                }

                if (key === "Background" && this.childs[key] instanceof SwitchElement) {
                    this._background = this.childs[key];
                }

                if (key === "LogoBlackTransition" && this.childs[key] instanceof SwitchElement) {
                    this._logoBlackTransition = this.childs[key];
                }

                if (key === "Transition" && this.childs[key] instanceof SwitchElement) {
                    this._transition = this.childs[key];
                }

                if (key === "ExpandingWildReel0" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ expandingWildReel0: this.childs[key] });
                    }
                }

                if (key === "ExpandingWildMask" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ expandingWildMask: this.childs[key] });
                    }
                }

                if (key === "ExpandingWildReel1" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ expandingWildReel1: this.childs[key] });
                    }
                }

                if (key === "ExpandingWildReel2" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ expandingWildReel2: this.childs[key] });
                    }
                }

                if (key === "ExpandingWildReel3" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ expandingWildReel3: this.childs[key] });
                    }
                }

                if (key === "ExpandingWildReel4" && this.childs[key] instanceof SwitchElement) {
                    if (this._teasingBonus != null) {
                        this._teasingBonus.addElementInstance({ expandingWildReel4: this.childs[key] });
                    }
                }
            }

            this._fsMessageStart = document.getElementById("fsMessageStart");
            this._fsMessageEnd = document.getElementById("fsMessageEnd");
            this._fsMessageEndNoWin = document.getElementById("fsMessageEndNoWin");

            //prevent first animation on page load
            this._fsMessageStart.classList.add("_noAnim");
            this._fsMessageEnd.classList.add("_noAnim");
            this._fsMessageEndNoWin.classList.add("_noAnim");

            if (DEBUG) {
                if (this._paylines == null) {
                    this.log("WARNING no paylines");
                }

                if (this._symbols == null) {
                    this.log("ERROR no symbols");
                }

                if (this._machine == null) {
                    this.log("ERROR no machine");
                }

                if (this._winAnimator == null) {
                    this.log("WARNING no winAnimator");
                }

                if (this._fsMessageStart == null) {
                    this.log("ERROR no fsMessageStart");
                }

                if (this._fsMessageEnd == null) {
                    this.log("ERROR no fsMessageEnd");
                }

                if (this._fsMessageEndNoWin == null) {
                    this.log("ERROR no fsMessageEndNoWin");
                }
            }
        }
    }

    disableLogo(): void {
        if (this.childs["Logo"] != null) {
            this.removeChild(this.childs["Logo"]);
        }
    }

    initReceived(controller: FreespinsController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        this._controller = controller;

        if (this._symbols != null) {
            if (this._machine != null) {
                if (GameManager.freespinsResponse != null) {
                    if (
                        GameManager.freespinsResponse.initData != null &&
                        GameManager.freespinsResponse.initData.reels !== null
                    ) {
                        this._machine.on(Machine.ReelStopped, this.onReelStopped.bind(this));
                        this._machine.on(Machine.MachineStopped, this.machineStopped.bind(this));
                        this._machine.initialize(this._symbols, GameManager.freespinsResponse.initData.reels);
                        this._machine.addElementInstance({ teasing: [this._teasingBonus] });
                    }

                    if (this._winAnimator != null) {
                        this._winAnimator.on(WinAnimator.FinishWinCalculation, this.finishWinCalculation.bind(this));
                        this._winAnimator.on(WinAnimator.FinishWinAnimator, this.finishWinAnimator.bind(this));
                        this._winAnimator.on(
                            WinAnimator.FinishQuickWinCalculation,
                            this.finishQuickWinCalculation.bind(this),
                        );
                        this._winAnimator.initialize(
                            this._symbols,
                            this._paylines,
                            this._noWinContainer,
                            this._winContainer,
                            this,
                        );
                        this._winAnimator.addElementInstance({ teasing: [this._teasingBonus] });
                    }
                } else {
                    if (DEBUG) {
                        this.log("ERROR no freespin reponse");
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no machine");
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no symbols");
            }
        }

        this.visible = false;
    }

    initIntro(): void {
        StageController.baseStage = false;
        StageController.freespinStage = true;

        this.allSwitchElementToCase("INIT_INTRO");

        this._machine.setStoppedSymbolsArray(GameManager.freespinsResponse.screen.reels);
        this._winAnimator.showSymbols(GameManager.freespinsResponse.screen.reels);
        this._winAnimator.showInitWins(GameManager.freespinsResponse.wins, GameManager.freespinsResponse.currentWin);

        this.visible = true;

        AmbientBackground.changeAmbientBackground("color-1", 0);

        this.setVisibleAllElements(true); // all element visible = true

        this.retrigger();

        if (GameManager.freespinsResponse.currentWin > 0) {
            if (this._background != null && this._background.getCurrentCase() === "NORMAL") {
                this._background.goToCase("WIN");
            }
            //PlayList.loop('SND_CycleLoop');
        } else {
            //PlayList.stop('SND_CycleLoop');
        }

        if (
            GameManager.freespinsResponse &&
            GameManager.freespinsResponse.screen &&
            GameManager.freespinsResponse.screen.reels !== null &&
            GameManager.freespinsResponse.screen.reels !== undefined
        ) {
            for (let i: number = 0; i < GameManager.freespinsResponse.screen.reels.length; i++) {
                if (this.hasReelExpandingWild(i)) {
                    this.childs["ExpandingWildReel" + i].goToCase("SHOWN");
                    this._initWild[i] = true;
                }
            }
        }

        if (this.isLastFreespin()) {
            PlayList.stop("SND_FSLoop");
            PlayList.loop("SND_CycleLoop");
            // PlayList.play('SND_FSEnd');

            PlayList.play("SND_BaseMessage");

            if (GameManager.freespinsResponse.totalWin > 0) {
                if (this._fsMessageEnd != null && this._fsMessageEnd.classList.contains(HtmlController.DisabledClass)) {
                    this._fsMessageEnd.classList.remove(HtmlController.DisabledClass);
                    this._fsMessageEnd.classList.remove("_noAnim");
                }
            } else {
                if (
                    this._fsMessageEndNoWin != null &&
                    this._fsMessageEndNoWin.classList.contains(HtmlController.DisabledClass)
                ) {
                    this._fsMessageEndNoWin.classList.remove(HtmlController.DisabledClass);
                    this._fsMessageEndNoWin.classList.remove("_noAnim");
                }
            }

            if (this._logoBlackTransition != null) {
                this._logoBlackTransition.visible = true; // logo and black transition visible = true
                this._logoBlackTransition.goToCase("SHOWING");
            }
        } else {
            PlayList.loop("SND_FSLoop");
        }
    }

    private hasReelExpandingWild(reelIndex: number): boolean {
        let expandingWild = false;
        if (
            GameManager.freespinsResponse.screen.reels !== null &&
            GameManager.freespinsResponse.screen.reels !== undefined
        ) {
            if (
                GameManager.freespinsResponse.screen.reels[reelIndex].transform !== null &&
                GameManager.freespinsResponse.screen.reels[reelIndex].transform !== undefined
            ) {
                for (
                    let i: number = 0;
                    i < GameManager.freespinsResponse.screen.reels[reelIndex].transform.length;
                    i++
                ) {
                    if (GameManager.freespinsResponse.screen.reels[reelIndex].transform[i] > 0) {
                        expandingWild = true;
                        break;
                    }
                }
            }
        }

        return expandingWild;
    }

    spin(): void {
        if (this._background != null && this._background.getCurrentCase() === "WIN") {
            this._background.goToCase("WIN_TO_NORMAL");
        }

        StageController.baseStage = false;
        StageController.freespinStage = true;

        for (let i: number = 0; i < this._initWild.length; i++) {
            if (this._initWild[i]) {
                this.childs["ExpandingWildReel" + i].goToCase("HIDE");
                this._initWild[i] = false;
            }
        }

        PlayList.loop("SND_FSLoop");

        if (this._fsMessageStart != null && !this._fsMessageStart.classList.contains(HtmlController.DisabledClass)) {
            this._fsMessageStart.classList.add(HtmlController.DisabledClass);
        }
        this._machine.spinStart(this._winAnimator.reset(false, true));
        // this._machine.spinStart(this._winAnimator.reset(true, true));
    }

    stop(): void {
        this._machine.spinStop(null, true);
    }

    startReceived(): void {
        if (this._fsMessageStart != null && !this._fsMessageStart.classList.contains(HtmlController.DisabledClass)) {
            this._fsMessageStart.classList.add(HtmlController.DisabledClass);
        }

        if (this._logoBlackTransition != null) {
            this._logoBlackTransition.visible = true;
            this._logoBlackTransition.goToCase("HIDING");
        }

        PlayList.stop("SND_CycleLoop");

        this._machine.setStoppedSymbolsArray(GameManager.freespinsResponse.screen.reels);
        this._winAnimator.showSymbols(GameManager.freespinsResponse.screen.reels);
        this._winAnimator.reset(true);
    }

    spinReceived(): void {
        this._machine.setStoppedSymbolsArray(GameManager.freespinsResponse.screen.reels);
        this._machine.spinStop(null, false, true);
    }

    endReceived(): void {}

    onReelStopped(): void {
        this._winAnimator.onReelStopped(GameManager.freespinsResponse.screen.reels, this._machine.actualReelStopped);
    }

    public getRandom(min: number, max: number): number {
        return Math.random() * (max - min) + min;
    }

    machineStopped(): void {
        if (GameManager.freespinsResponse.currentWin > 0) {
            if (this._background != null && this._background.getCurrentCase() === "NORMAL") {
                this._background.goToCase("WIN");
            }
        }
        this._controller.machineStopped();

        if (this._winAnimator.showWins(GameManager.freespinsResponse.wins, GameManager.freespinsResponse.currentWin)) {
            PlayList.stop("SND_FSLoop");
        } else {
            PlayList.loop("SND_FSLoop");
        }

        PlayList.stop("SND_BaseWildLoop", 20);

        if (GameManager.freespinsResponse.currentWin === 0) {
            if (this.isLastFreespin()) {
                PlayList.stop("SND_FSLoop");
                PlayList.loop("SND_CycleLoop");
                // PlayList.play('SND_FSEnd');

                PlayList.play("SND_BaseMessage");

                if (GameManager.freespinsResponse.totalWin > 0) {
                    if (
                        this._fsMessageEnd != null &&
                        this._fsMessageEnd.classList.contains(HtmlController.DisabledClass)
                    ) {
                        this._fsMessageEnd.classList.remove(HtmlController.DisabledClass);
                        this._fsMessageEnd.classList.remove("_noAnim");
                    }
                } else {
                    if (
                        this._fsMessageEndNoWin != null &&
                        this._fsMessageEndNoWin.classList.contains(HtmlController.DisabledClass)
                    ) {
                        this._fsMessageEndNoWin.classList.remove(HtmlController.DisabledClass);
                        this._fsMessageEndNoWin.classList.remove("_noAnim");
                    }
                }

                if (this._logoBlackTransition != null) {
                    this._logoBlackTransition.visible = true; // logo and black transition visible = true
                    this._logoBlackTransition.goToCase("SHOWING");
                }
            }
        }
    }

    private finishQuickWinCalculation(): void {
        this._controller.quickWinCalculationFinished();
    }

    finishWinCalculation(): void {
        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        this._controller.allWinsFinished();

        if (this.isLastFreespin()) {
            PlayList.stop("SND_FSLoop");
            PlayList.loop("SND_CycleLoop");
            // PlayList.play('SND_FSEnd');

            PlayList.play("SND_BaseMessage");

            if (GameManager.freespinsResponse.totalWin > 0) {
                if (this._fsMessageEnd != null && this._fsMessageEnd.classList.contains(HtmlController.DisabledClass)) {
                    this._fsMessageEnd.classList.remove(HtmlController.DisabledClass);
                    this._fsMessageEnd.classList.remove("_noAnim");
                }
            } else {
                if (
                    this._fsMessageEndNoWin != null &&
                    this._fsMessageEndNoWin.classList.contains(HtmlController.DisabledClass)
                ) {
                    this._fsMessageEndNoWin.classList.remove(HtmlController.DisabledClass);
                    this._fsMessageEndNoWin.classList.remove("_noAnim");
                }
            }

            if (this._logoBlackTransition != null) {
                this._logoBlackTransition.visible = true; // logo and black transition visible = true
                this._logoBlackTransition.goToCase("SHOWING");
            }
        } else {
            // PlayList.loop('SND_FSLoop');
            if (!DataManager.Autoplay) {
                PlayList.loop("SND_FSLoop");
                //PlayList.loop('SND_CycleLoop');
            } else {
                PlayList.loop("SND_FSLoop");
            }
        }

        this.retrigger();
    }

    intro(long: boolean = false): void {
        StageController.freespinStage = true;

        this.visible = true;
        this.setVisibleAllElements(true); // all element visible = true

        // this.childs['Paylines'].alpha = 0;
        // this.childs['WinContainer'].alpha = 0;
        // this.childs['WinAnimator'].alpha = 0;

        if (this.childs["Logo"]) {
            this.childs["Logo"].alpha = 0;
        }

        this.allSwitchElementToCase("INTRO");

        if (this._transition != null) {
            this._transition.goToCase("FS_SHOW");
        }

        this._introDelay.start(FreespinsView.INTRO_DELAY);
    }

    introFinished(): void {
        StageController.baseStage = false;
        StageController.freespinStage = true;

        this.visible = true;
        this.setVisibleAllElements(true); // all element visible = true

        if (this._transition != null) {
            this._transition.goToCase("FS_INTRO");
        }

        PlayList.loop("SND_FSLoop");

        this._controller.introFinished();
    }

    outro(): void {
        StageController.freespinStage = true;

        this.visible = true;

        AmbientBackground.changeAmbientBackground("base", 90);

        this.setVisibleAllElements(true); // all element visible = true

        PlayList.stop("SND_FSLoop");
        PlayList.stop("SND_CycleLoop");
        PlayList.play("SND_Transition");

        if (this._fsMessageEnd != null && !this._fsMessageEnd.classList.contains(HtmlController.DisabledClass)) {
            this._fsMessageEnd.classList.add(HtmlController.DisabledClass);
        }

        if (
            this._fsMessageEndNoWin != null &&
            !this._fsMessageEndNoWin.classList.contains(HtmlController.DisabledClass)
        ) {
            this._fsMessageEndNoWin.classList.add(HtmlController.DisabledClass);
        }

        if (this._logoBlackTransition != null) {
            this._logoBlackTransition.visible = true; // logo and black transition visible = true
            this._logoBlackTransition.goToCase("HIDING");
        }

        if (this._transition != null) {
            this._transition.goToCase("FS_OUTRO");
            PlayList.play("SND_FStransition");
        }

        if (this.childs["Logo"]) {
            Tweens.customTween(this.childs["Logo"], {
                tween: "alpha",
                tweenValues: [1, 0],
                tweenDuration: [30],
                tweenEase: ["linear"],
            });
        }

        this._outroDelay.start(FreespinsView.OUTRO_DELAY);
    }

    outroFinished(): void {
        StageController.freespinStage = false;

        this.visible = false;

        this.allSwitchElementToCase("EMPTY");

        this._winAnimator.reset(true);

        if (this._teasingBonus) {
            this._teasingBonus.hideExpandingWilds();
        }

        if (this._transition != null) {
            this._transition.goToCase("BASE_SHOW");
        }

        this._controller.outroFinished();
    }

    enable(): void {
        this.visible = true;
        this.setVisibleAllElements(false); // all element visible = false

        PlayList.stop("SND_BaseLoop");
        PlayList.loop("SND_CycleLoop");

        PlayList.play("SND_BaseMessage");

        if (this._fsMessageStart != null && this._fsMessageStart.classList.contains(HtmlController.DisabledClass)) {
            this._fsMessageStart.classList.remove(HtmlController.DisabledClass);
            this._fsMessageStart.classList.remove("_noAnim");
        }

        if (this._logoBlackTransition != null) {
            this._logoBlackTransition.visible = true; // logo and black transition visible = true
            this._logoBlackTransition.goToCase("SHOWING");
        }
    }

    private retrigger(): void {
        if (!this.isLastFreespin()) {
            if (GameManager.freespinsResponse.wins != null && GameManager.freespinsResponse.wins.length > 0) {
                for (let i: number = 0; i < GameManager.freespinsResponse.wins.length; i++) {
                    if (GameManager.freespinsResponse.wins[i].type === Communication.Game.WinType.freeSpins) {
                        if (
                            this._fsMessageStart != null &&
                            this._fsMessageStart.classList.contains(HtmlController.DisabledClass)
                        ) {
                            this._fsMessageStart.classList.remove(HtmlController.DisabledClass);
                            this._fsMessageStart.classList.remove("_noAnim");
                        }
                    }
                }
            }
        }
    }

    private isLastFreespin(): boolean {
        if (DataManager.GameLimit) {
            return true;
        }

        return (
            GameManager.freespinsResponse.freeSpins != null &&
            GameManager.freespinsResponse.freeSpins.remainingCount != null &&
            GameManager.freespinsResponse.freeSpins.remainingCount === 0
        );
    }

    private allSwitchElementToCase(caseType: string): void {
        for (let key in this.childs) {
            if (this.childs[key] instanceof SwitchElement) {
                let elem: SwitchElement = this.childs[key];

                if (elem.hasCase(caseType)) {
                    elem.goToCase(caseType);
                }
            }
        }
    }

    private setVisibleAllElements(visible: boolean): void {
        for (let key in this.childs) {
            this.childs[key].visible = visible;
        }
    }

    private machineSpinStopStart(): void {}
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Freespins/FreespinsView.ts
 **/